export const BASE_URL = import.meta.env.VITE_API_URL;

/**
 * 서버가 내려준 에러 응답을 감싸는 Error 서브클래스.
 *
 * 기존에는 `throw errorResponse`로 파싱된 JSON 객체(`{ code, message }`)를
 * 그대로 던졌는데, 이는 Error 인스턴스가 아니라서:
 *  - Sentry/`String(error)`에서 "[object Object]"로만 찍혀 원인이 가려지고,
 *  - `err instanceof Error` 분기를 전부 빗나가 서버 메시지가 버려졌다.
 * (실제 사례: 마감된 과제 제출 시 "제출 마감 시간이 지났습니다." 메시지가
 *  학생에게 안내되지 않고 일반 오류처럼 보였음)
 *
 * 이제 status/code/원본 payload를 보존한 채 server message를 `.message`로 노출한다.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly payload: unknown;

  constructor(status: number, payload: unknown) {
    const isObj = payload != null && typeof payload === 'object';
    const message =
      isObj && 'message' in payload
        ? String((payload as { message: unknown }).message)
        : '요청 처리에 실패했습니다.';
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code =
      isObj && 'code' in payload
        ? String((payload as { code: unknown }).code)
        : undefined;
    this.payload = payload;
  }
}

let refreshPromise: Promise<boolean> | null = null;

interface ApiOptions {
  method: 'GET' | 'POST' | 'DELETE' | 'PUT' | 'PATCH';
  data?: unknown;
  headers?: Record<string, string>;
  cache?: 'force-cache' | 'no-store';
  timeout?: number;
}

async function refreshAccessToken(): Promise<boolean> {
  try {
    const res = await fetch(`${BASE_URL}/student/auth/refresh`, {
      method: 'POST',
      credentials: 'include', // refresh_token은 httpOnly 쿠키
    });

    if (!res.ok) return false;
    await res.json();
    return true;
  } catch (err) {
    console.error('Refresh token failed', err);
    return false;
  }
}

const api = async <R, D>(
  url: string,
  options?: ApiOptions & { data?: D },
  retry = true, // 내부 재시도 여부
): Promise<R> => {
  const { method, data, headers, timeout = 10000 } = options || {};

  const fetchOptions: RequestInit = {
    method,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...headers, // 사용자 정의 헤더 병합
    },
    signal: AbortSignal.timeout(timeout),
  };

  if (data) {
    fetchOptions.body = JSON.stringify(data); // Serialize data if present
  }

  const response = await fetch(`${BASE_URL}${url}`, fetchOptions);

  if (response.status === 401 && retry) {
    if (!refreshPromise) {
      refreshPromise = refreshAccessToken().finally(() => {
        refreshPromise = null;
      });
    }

    const refreshed = await refreshPromise;

    if (refreshed) {
      // retry once after refreshing
      return api<R, D>(url, options, false);
    } else {
      if (typeof window !== 'undefined') {
        window.location.href = '/sign-in'; // 클라이언트라면 로그인 이동
      }
      throw new Error('Unauthorized');
    }
  }

  // Check for errors
  if (!response.ok) {
    let errorResponse: unknown;
    try {
      errorResponse = await response.json();
    } catch {
      errorResponse = undefined; // JSON 본문이 없거나 파싱 실패한 경우
    }
    // ApiError(Error 서브클래스)로 던져 server message/status/code를 보존한다.
    throw new ApiError(response.status, errorResponse); // This will trigger `onError`
  }

  return (await response.json()) as Promise<R>;
};

export default api;
